/**
 * Fabrication Panel Component
 * Upload PLY/GLB, select intent profile, trigger fabrication
 */

import { useState, useCallback, useRef } from 'react';
import { useFabStore } from '../store';
import { FABRICATION_PROFILES } from '../types';
import { fabricateAsset } from '../services/fabrication';

export function FabricationPanel() {
  const {
    selectedProfile,
    setSelectedProfile,
    isProcessing,
    setIsProcessing,
    setRawModelUrl,
    setCurrentAsset,
    setCurrentJob,
  } = useFabStore();

  const [inputUrl, setInputUrl] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  }, []);

  const handleFile = useCallback((file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext !== 'ply' && ext !== 'glb') {
      setError('Please upload a PLY or GLB file');
      return;
    }

    setError(null);
    const url = URL.createObjectURL(file);
    setRawModelUrl(url);
    setCurrentAsset({
      asset_id: crypto.randomUUID(),
      source: 'upload',
      input_format: ext as 'ply' | 'glb',
      raw_url: url,
      created_at: new Date().toISOString(),
    });
  }, [setRawModelUrl, setCurrentAsset]);

  const handleUrlSubmit = useCallback(() => {
    if (!inputUrl.trim()) {
      setError('Please enter a URL');
      return;
    }

    const ext = inputUrl.split('.').pop()?.toLowerCase().split('?')[0];
    if (ext !== 'ply' && ext !== 'glb') {
      setError('URL must point to a PLY or GLB file');
      return;
    }

    setError(null);
    setRawModelUrl(inputUrl);
    setCurrentAsset({
      asset_id: crypto.randomUUID(),
      source: 'url',
      input_format: ext as 'ply' | 'glb',
      raw_url: inputUrl,
      created_at: new Date().toISOString(),
    });
  }, [inputUrl, setRawModelUrl, setCurrentAsset]);

  const handleFabricate = useCallback(async () => {
    const { currentAsset } = useFabStore.getState();
    if (!currentAsset) {
      setError('Please upload or provide a model first');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const job = await fabricateAsset(currentAsset, selectedProfile);
      setCurrentJob(job);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fabrication failed');
    } finally {
      setIsProcessing(false);
    }
  }, [selectedProfile, setIsProcessing, setCurrentJob]);

  return (
    <div className="p-4 flex-1 overflow-y-auto">
      <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <span>⚙️</span> Fabrication
      </h2>

      {/* Upload Zone */}
      <div
        className={`
          border-2 border-dashed rounded-lg p-6 mb-4 text-center
          transition-colors cursor-pointer
          ${dragActive
            ? 'border-fab-accent bg-fab-accent/10'
            : 'border-slate-600 hover:border-slate-500'
          }
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".ply,.glb"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
        />
        <div className="text-3xl mb-2">📁</div>
        <p className="text-sm text-slate-300">
          Drop PLY / GLB file here
        </p>
        <p className="text-xs text-slate-500 mt-1">
          or click to browse
        </p>
      </div>

      {/* URL Input */}
      <div className="mb-4">
        <label className="block text-xs text-slate-400 mb-1">
          Or paste URL
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={inputUrl}
            onChange={(e) => setInputUrl(e.target.value)}
            placeholder="https://example.com/model.glb"
            className="flex-1 bg-slate-800 border border-slate-600 rounded px-3 py-2
                       text-sm text-white placeholder-slate-500
                       focus:outline-none focus:border-fab-accent"
          />
          <button
            onClick={handleUrlSubmit}
            className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded
                       text-sm text-white transition-colors"
          >
            Load
          </button>
        </div>
      </div>

      {/* Intent Profile Selection */}
      <div className="mb-4">
        <label className="block text-xs text-slate-400 mb-2">
          Intent Profile
        </label>
        <div className="space-y-2">
          {Object.values(FABRICATION_PROFILES).map((profile) => (
            <button
              key={profile.id}
              onClick={() => setSelectedProfile(profile.id)}
              className={`
                w-full text-left p-3 rounded-lg border transition-all
                ${selectedProfile === profile.id
                  ? 'border-fab-primary bg-fab-primary/20 text-white'
                  : 'border-slate-600 bg-slate-800/50 text-slate-300 hover:border-slate-500'
                }
              `}
            >
              <div className="font-medium text-sm">{profile.name}</div>
              <div className="text-xs text-slate-400 mt-0.5">
                {profile.description}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {profile.target_triangles.toLocaleString()} tris • {profile.texture_resolution}px
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-fab-error/20 border border-fab-error/50 rounded-lg">
          <p className="text-sm text-fab-error">{error}</p>
        </div>
      )}

      {/* Fabricate Button */}
      <button
        onClick={handleFabricate}
        disabled={isProcessing}
        className={`
          w-full py-3 rounded-lg font-semibold text-white
          transition-all flex items-center justify-center gap-2
          ${isProcessing
            ? 'bg-slate-600 cursor-not-allowed'
            : 'bg-gradient-to-r from-fab-primary to-fab-secondary hover:opacity-90'
          }
        `}
      >
        {isProcessing ? (
          <>
            <span className="animate-spin">⚙️</span>
            Processing...
          </>
        ) : (
          <>
            <span>🚀</span>
            Fabricate
          </>
        )}
      </button>

      <p className="text-xs text-slate-500 text-center mt-2">
        Powered by Raindrop + Vultr
      </p>
    </div>
  );
}
